import { useEffect, useRef, useState } from "react";
import { Button, DatePicker, Input, Modal, Space } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import {
  getRejectedBuyAndSupplyOrdersApi,
  makeBuyAndSupplyOrderAsPendingApi,
} from "Network";
import { toastError, toastSuccess } from "helpers/toasters";
import { exportDataAsExcel } from "helpers/helpfulFunctions";

import Header from "modules/SubComponents/Header";
import SubHeader from "modules/SubComponents/SubHeader";
import GridTable from "modules/SubComponents/GridTable/GridTable";

function RejectedBuyAndSupplyOrders() {
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [renderMe, setRenderMe] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [rowData, setRowData] = useState(null);
  const [searchValues, setSearchValues] = useState({
    userName: "",
    userMobile: "",
    item: "",
    quantity: "",
    remainingQuantity: "",
    unitPrice: "",
    totalPrice: "",
    paymentType: "",
    from: "",
    to: "",
    salesPerson: "",
  });
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedRange, setSelectedRange] = useState([]);
  const { RangePicker } = DatePicker;
  const searchInput = useRef(null);

  const updateSearchValues = (property, newValue, otherProperty) => {
    const copyOfSearchObj = { ...searchValues };
    copyOfSearchObj[property] = newValue;
    if (otherProperty) {
      copyOfSearchObj[otherProperty.property] = otherProperty.value;
    }
    setSearchValues(copyOfSearchObj);
  };

  const handleDateChange = (dates) => {
    setSelectedRange(dates);
    if (dates) {
      const startDate = dates[0].format("YYYY-MM-DD");
      const endDate = dates[1].format("YYYY-MM-DD");
      updateSearchValues("from", startDate, { property: "to", value: endDate });
    } else {
      updateSearchValues("from", "", { property: "to", value: "" });
    }
  };

  const handleExportDataToExcel = () => {
    if (isExporting) return;
    setIsExporting(true);
    const baseProps = [
      { title: "Name", prop: "fullName" },
      { title: "Mobile", prop: "phone" },
      { title: "ID Number", prop: "idNumber" },
      { title: "Item", prop: "item" },
      { title: "Quantity", prop: "quantity" },
      { title: "Unit Price", prop: "unitPrice" },
      { title: "Total Price", prop: "price" },
      { title: "Sales Person", prop: "salesPerson" },
      { title: "Order Date", prop: "creationDate" },
      { title: "Management Fees", prop: "managementFees" },
      { title: "Payment Type", prop: "paymentType" },
    ];
    const payload = {
      isExport: true,
      pageNumber: currentPage,
      perPage: pageSize,
      ...searchValues,
    };
    for (let property in payload) {
      if (
        !payload[property] &&
        payload[property] !== 0 &&
        payload[property] !== false
      ) {
        delete payload[property];
      }
    }
    getRejectedBuyAndSupplyOrdersApi(
      payload,
      (res) => {
        if (res?.success) {
          exportDataAsExcel(res.data.orders, {
            baseProps,
            tab: "Rejected Buy and Supply Orders Report",
          });
          setIsExporting(false);
        } else {
          setIsExporting(false);
          toastError(res?.message);
        }
      },
      (fail) => {
        setIsExporting(false);
        toastError(fail?.err_msg || fail?.message);
      },
    );
  };

  const handlePageChange = (page) => {
    setLoading(true);
    setCurrentPage(page);
  };

  const handlePageSizeChange = (current, newSize) => {
    setLoading(true);
    setPageSize(newSize);
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    setLoading(true);
    switch (dataIndex) {
      case "fullName":
        updateSearchValues("userName", selectedKeys[0]);
        break;
      case "phone":
        updateSearchValues("userMobile", selectedKeys[0]);
        break;
      case "item":
        updateSearchValues("item", selectedKeys[0]);
        break;
      case "quantity":
        updateSearchValues("quantity", selectedKeys[0]);
        break;
      case "remainingQuantity":
        updateSearchValues("remainingQuantity", selectedKeys[0]);
        break;
      case "unitPrice":
        updateSearchValues("unitPrice", selectedKeys[0]);
        break;
      case "price":
        updateSearchValues("totalPrice", selectedKeys[0]);
        break;
      case "paymentType":
        updateSearchValues("paymentType", selectedKeys[0]);
        break;
      case "salesPerson":
        updateSearchValues("salesPerson", selectedKeys[0]);
        break;
      default:
        break;
    }
    confirm();
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    setLoading(true);
    switch (dataIndex) {
      case "fullName":
        updateSearchValues("userName", "");
        break;
      case "phone":
        updateSearchValues("userMobile", "");
        break;
      case "item":
        updateSearchValues("item", "");
        break;
      case "quantity":
        updateSearchValues("quantity", "");
        break;
      case "remainingQuantity":
        updateSearchValues("remainingQuantity", "");
        break;
      case "unitPrice":
        updateSearchValues("unitPrice", "");
        break;
      case "price":
        updateSearchValues("totalPrice", "");
        break;
      case "paymentType":
        updateSearchValues("paymentType", "");
        break;
      case "salesPerson":
        updateSearchValues("salesPerson", "");
        break;
      default:
        break;
    }
    clearFilters();
    confirm();
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, dataIndex, confirm)
            }
            size="small"
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => true,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const getColumnDateProps = (dataIndex) => ({
    filterDropdown: () => (
      <div
        style={{
          padding: 8,
        }}
        className="range-picker-holder"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <RangePicker
          value={selectedRange}
          allowClear
          popupClassName="all-orders-date-picker"
          inputReadOnly={false}
          onChange={handleDateChange}
          onKeyDown={(e) => e.preventDefault()}
        />
      </div>
    ),
    filterIcon: (filtered) => <FilterOutlined />,
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => text,
  });

  const openRevokeModal = (row) => {
    setRowData(row);
    setIsModalOpen(true);
  };

  const handleCancelModal = () => {
    setRowData(null);
    setIsModalOpen(false);
  };

  const handleConfirmRevokeToPending = () => {
    if (!rowData?.id || isRevoking) return;
    setIsRevoking(true);
    makeBuyAndSupplyOrderAsPendingApi(
      { orderId: rowData?.id },
      (success) => {
        if (success?.success) {
          toastSuccess(success.message);
          setRenderMe((prev) => !prev);
        } else {
          toastError(success.message);
        }
        setIsRevoking(false);
        setIsModalOpen(false);
        setRowData(null);
      },
      (fail) => {
        toastError(fail?.message || fail?.err_msg);
        setIsRevoking(false);
      },
    );
  };

  const columns = [
    {
      key: "fullName",
      title: "NAME",
      dataIndex: "fullName",
      ...getColumnSearchProps("fullName"),
    },
    {
      key: "phone",
      title: "MOBILE",
      dataIndex: "phone",
      ...getColumnSearchProps("phone"),
    },
    {
      key: "idNumber",
      title: "ID NUMBER",
      dataIndex: "idNumber",
    },
    {
      key: "item",
      title: "ITEM",
      dataIndex: "item",
      ...getColumnSearchProps("item"),
    },
    {
      key: "quantity",
      title: "QUANTITY",
      dataIndex: "quantity",
      ...getColumnSearchProps("quantity"),
    },
    {
      key: "unitPrice",
      title: "UNIT PRICE",
      dataIndex: "unitPrice",
      ...getColumnSearchProps("unitPrice"),
    },
    {
      key: "price",
      title: "TOTAL PRICE",
      dataIndex: "price",
      ...getColumnSearchProps("price"),
    },
    {
      key: "salesPerson",
      title: "SALES PERSON",
      dataIndex: "salesPerson",
      ...getColumnSearchProps("salesPerson"),
    },
    {
      key: "creationDate",
      title: "ORDER DATE",
      dataIndex: "creationDate",
      ...getColumnDateProps("creationDate"),
    },
    {
      key: "managementFees",
      title: "MANAGEMENT FEES",
      dataIndex: "managementFees",
      render: (managementFees) =>
        managementFees ? `${managementFees} EGP` : "",
    },
    {
      key: "paymentType",
      title: "PAYMENT TYPE",
      dataIndex: "paymentType",
      ...getColumnSearchProps("paymentType"),
    },
    {
      key: "id",
      title: "ACTIONS",
      dataIndex: "action",
      render: (_, row) => (
        <Button type="primary" onClick={() => openRevokeModal(row)}>
          Revoke To Pending
        </Button>
      ),
    },
  ];

  useEffect(() => {
    const payload = {
      pageNumber: currentPage,
      perPage: pageSize,
      ...searchValues,
    };
    for (let property in payload) {
      if (!payload[property] && payload[property] !== 0) {
        delete payload[property];
      }
    }
    getRejectedBuyAndSupplyOrdersApi(
      payload,
      (res) => {
        if (res?.success) {
          setLoading(false);
          setDataSource(res.data.orders);
          setTotalCount(res.data.totalCount);
        } else {
          setLoading(false);
          toastError(res?.message);
        }
      },
      (fail) => {
        setLoading(false);
        toastError(fail?.err_msg || fail?.message);
      },
    );
  }, [renderMe, currentPage, searchValues, pageSize]);

  return (
    <section className="users">
      <div className="users__all-contents ">
        <div className="users__content">
          <div className="users__usersData">
            <Header title="Rejected Buy and Supply Orders" />
            <SubHeader
              bgColor="#356ab3"
              title="All Rejected Buy and Supply Orders "
            />
            <div className="users__table grid-table mrg-ye-50 ">
              <GridTable
                loading={loading}
                columns={columns}
                dataSource={dataSource}
                rowId={"id"}
                allowExport={true}
                apiExport={true}
                isExporting={isExporting}
                onExport={handleExportDataToExcel}
                title={"Rejected Buy and Supply Orders"}
                currentPage={currentPage}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </div>
        </div>
      </div>
      {isModalOpen && (
        <Modal
          title="Revoke Order To Pending"
          open={isModalOpen}
          okText="Confirm"
          cancelText="Cancel"
          onOk={handleConfirmRevokeToPending}
          confirmLoading={isRevoking}
          onCancel={handleCancelModal}
        >
          <p>
            Are you sure you want to revoke this rejected order back to pending?
          </p>
        </Modal>
      )}
    </section>
  );
}

export default RejectedBuyAndSupplyOrders;
